import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Fade,
  Slide,
  Button,
  IconButton,
} from "@mui/material";
import PtwGuideDialog from "@/components/dialogs/PtwGuideDialog";
import { useTheme, alpha } from "@mui/material/styles";
import MyStepper from "./MyStepper";
import Toast from "@/components/toast";
import { MyFormData } from "./form-context";
import PermitInfo from "./WorkPermitInfo";
import OtherResources from "./OtherResources";
import HazidNew from "./HazidNew";
import RiskAssessmentNew from "./RiskAssessmentNew";
import Commitment from "./WorkPermitCommitment";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CheckCircleOutline,
  Description,
  Group,
  Assignment,
  Assessment,
  TaskAlt,
  HelpOutline,
} from "@mui/icons-material";

function FormLayout() {
  const { t } = useTranslation();
  const theme = useTheme();
  const context = MyFormData();
  const navigate = useNavigate();
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  // Scroll to top when step changes
  useEffect(() => {
    if (context?.currentStep) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [context?.currentStep]);

  if (!context) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6" sx={{ textAlign: "center" }}>
          {t("formLayout.errorContext")}
        </Typography>
      </Box>
    );
  }

  const {
    currentStep,
    handleSubmit,
    steps,

    complete,
    toast,
  } = context;

  // Step configurations with translated titles and descriptions
  const stepConfigs = [
    {
      icon: <Description />,
      title: t("formLayout.step1Title"),
      description: t("formLayout.step1Description"),
      component: <PermitInfo />,
    },
    {
      icon: <Group />,
      title: t("formLayout.step2Title"),
      description: t("formLayout.step2Description"),
      component: <OtherResources />,
    },
    {
      icon: <Assignment />,
      title: t("formLayout.step3Title"),
      description: t("formLayout.step3Description"),
      component: <HazidNew />,
    },
    {
      icon: <Assessment />,
      title: t("formLayout.step4Title"),
      description: t("formLayout.step4Description"),
      component: <RiskAssessmentNew />,
    },
    {
      icon: <TaskAlt />,
      title: t("formLayout.step5Title"),
      description: t("formLayout.step5Description"),
      component: <Commitment />,
    },
  ];

  const currentStepConfig = stepConfigs[currentStep - 1];

  const getCardStyles = (isActive = false) => ({
    borderRadius: 4,
    boxShadow: isActive
      ? `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`
      : `0 4px 16px ${alpha(theme.palette.common.black, 0.1)}`,
    border: `2px solid ${isActive ? theme.palette.primary.main : "transparent"}`,
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(135deg, ${theme.palette.background.paper}, ${alpha(theme.palette.primary.main, 0.05)})`
        : `linear-gradient(135deg, ${theme.palette.background.paper}, ${alpha(theme.palette.primary.main, 0.02)})`,
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
    "&::before": isActive
      ? {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
          borderRadius: "16px 16px 0 0",
        }
      : {},
  });

  return (
    <>
      <Box
        sx={{
          maxWidth: 1200,
          margin: "0 auto",
          p: 1,
          minHeight: "100vh",
          position: "relative",
          pb: 12,
        }}
      >
        <form
          onSubmit={handleSubmit}
          noValidate
          aria-label={t("formLayout.formAria")}
        >
          {!complete && currentStep <= 5 && (
            <Slide direction="left" in timeout={500} key={currentStep}>
              <Card
                sx={{
                  ...getCardStyles(true),
                  minHeight: 400,
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  {/* Step Header */}
                  <Box
                    sx={{
                      background: `linear-gradient(135deg, 
                      ${alpha(theme.palette.primary.main, 0.1)}, 
                      ${alpha(theme.palette.primary.light, 0.05)}
                    )`,
                      p: 3,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                        }}
                      >
                        {currentStepConfig?.icon}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            mb: 0.5,
                          }}
                        >
                          {currentStepConfig?.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: "0.875rem",
                          }}
                        >
                          {currentStepConfig?.description}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", gap: 2, alignItems: "center" }}
                      >
                        <Box
                          sx={{
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.1
                            ),
                            color: theme.palette.primary.main,
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            fontSize: "0.875rem",
                            fontWeight: 600,
                          }}
                        >
                          {t("formLayout.stepCounter", {
                            current: currentStep,
                            total: steps.length,
                          })}
                        </Box>
                        <IconButton
                          onClick={() => setHelpModalOpen(true)}
                          sx={{
                            bgcolor: theme.palette.info.main,
                            color: theme.palette.info.contrastText,
                            boxShadow: theme.shadows[2],
                            "&:hover": {
                              bgcolor: theme.palette.info.dark,
                              boxShadow: theme.shadows[4],
                              transform: "scale(1.05)",
                            },
                            transition: "all 0.2s ease",
                          }}
                          aria-label={t("formLayout.helpButtonAria")}
                        >
                          <HelpOutline />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>

                  {/* Step Content */}
                  <Box
                    sx={{
                      p: 4,
                      // Ensure the last step content is tall enough so the stepper stays at the bottom
                      minHeight:
                        currentStep === steps.length
                          ? { xs: "40vh", md: "55vh" }
                          : undefined,
                    }}
                  >
                    <Fade in timeout={400}>
                      <Box>{currentStepConfig?.component}</Box>
                    </Fade>
                  </Box>
                </CardContent>
              </Card>
            </Slide>
          )}

          {/* Success State */}
          {complete && (
            <Fade in timeout={800}>
              <Card
                sx={{
                  ...getCardStyles(),
                  mt: 3,
                  background: `linear-gradient(135deg, 
                  ${alpha(theme.palette.success.main, 0.1)}, 
                  ${alpha(theme.palette.success.light, 0.05)}
                )`,
                  border: `2px solid ${theme.palette.success.main}`,
                }}
              >
                <CardContent
                  sx={{
                    textAlign: "center",
                    p: 6,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.success.main,
                      color: theme.palette.success.contrastText,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.3)}`,
                    }}
                  >
                    <CheckCircleOutline sx={{ fontSize: 40 }} />
                  </Box>

                  <Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.success.main,
                        mb: 1,
                      }}
                    >
                      {t("formLayout.successHeading")}
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t("formLayout.submittedMessage")}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexWrap: "wrap",
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        // Clear all localStorage data and reload page to start fresh
                        localStorage.removeItem("step_inputs");
                        localStorage.removeItem("workflowHazards");
                        localStorage.removeItem("duplicatedWorkflowHazards");
                        localStorage.removeItem("duplicatedFormData");
                        // Force page reload to reset all state
                        window.location.href = "/newptw";
                      }}
                      sx={{
                        borderRadius: 3,
                        px: 3,
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: "none",
                      }}
                    >
                      {t("formLayout.newPermitButton")}
                    </Button>

                    <Button
                      variant="contained"
                      color="primary"
                      /* onClick={() => {
                        window.location.assign("/mydocs");
                      }} */
                      onClick={() => navigate("/mydocs")}
                      sx={{
                        borderRadius: 3,
                        px: 3,
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: "none",
                      }}
                    >
                      {t("formLayout.viewDocumentsButton")}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          )}

          {/* Sticky Progress Bar */}
          {!complete && (
            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                left: 0,
                right: 0,
                mt: 2,
                pt: 0.9,
                pb: 0.3,
                px: 5,
                background:
                  theme.palette.mode === "dark"
                    ? `linear-gradient(to top, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`
                    : `linear-gradient(to top, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
                backdropFilter: "blur(12px)",
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: `0 -4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
                zIndex: 1000,
                maxWidth: 1200,
                mx: "auto",
                borderRadius: "16px 16px 0 0",
                transform: "translateY(2px)",
              }}
            >
              {/*  <Box sx={{ maxWidth: 800, mx: "auto", px: 0 }}> */}
              <MyStepper steps={steps} />
              {/*  </Box> */}
            </Box>
          )}
        </form>
      </Box>

      {/* User Guide Help Modal */}
      <PtwGuideDialog
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
      />

      <Toast
        open={toast.open}
        severity={toast.severity}
        message={toast.message}
      />
    </>
  );
}

export default FormLayout;
